/**
 * PATH: packages/email-hub/src/feature/signal-strip/SignalStrip.tsx
 * FILE_ID: EMAILHUB-FEATURE-SIGNALSTRIP-001
 * PURPOSE: Responsive reader metadata header with stable semantic lanes.
 * WHY:     The prior fixed two-row layout nested a row scroller, per-lane
 *          scrollers, and a workflow scroller. At split-reader widths this
 *          clipped Company and pushed Watchers off-screen. Lanes now use
 *          bounded grids and wrap vertically; the page is the only scroll axis.
 * LANDMINE: Right-click remains owned by the canonical EmailContextMenu.
 *           Scoped lane actions open only from the visible kebab buttons.
 * FLOW:    host pre-wires lanes + workflow -> width observer -> semantic grid.
 * DEPENDENCIES: @quotech/ui EMAIL_SIGNAL_LANE_TOKENS (merged contract,
 *          change #42) — LANE ORDER comes from EMAIL_SIGNAL_LANES so this
 *          surface cannot drift from the token contract.
 * RELATED:
 *   - ReaderSignalStrip.tsx: the email-hub host that wires live chips.
 *   - packages/ui/src/components/email/signalTokens.ts: hue + label tokens.
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { EMAIL_SIGNAL_LANES, EMAIL_SIGNAL_LANE_TOKENS, type EmailSignalLane } from '@quotech/ui';

import { cn } from '../../_internal/lib/utils';
import { densityForWidth, type SignalStripDensity } from './signalDensity';

/** One-line meaning per lane — title names the action boundary too. */
const LANE_TITLES: Record<EmailSignalLane, string> = {
  who: 'Company — CRM identity, people, and company actions',
  what: 'Classification — workflow category and category actions',
  signals: 'Intelligence — actionable micro-signals such as verification codes',
  where: 'Filed to — board and lane routing actions',
};

export interface SignalStripProps {
  who?: ReactNode;
  what?: ReactNode;
  signals?: ReactNode;
  where?: ReactNode;
  /** Owner, due-date, and watcher controls rendered beside Filed to. */
  workflow?: ReactNode;
  /** Visible `…` lane actions. Right-click is owned by the email context menu
   *  (BUG-2026-07-17-EMAIL-CONTEXTMENU-COVERAGE) — do not pair it here. */
  laneActions?: Partial<Record<EmailSignalLane, ReactNode>>;
  onLaneContextMenu?: (lane: EmailSignalLane, event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

function useSignalStripDensity(): [RefObject<HTMLDivElement | null>, SignalStripDensity] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [density, setDensity] = useState<SignalStripDensity>('compact');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setDensity(densityForWidth(element.clientWidth));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, density];
}

/** One labelled lane card: fixed label + chip run + visible actions. */
export function SignalLane({
  lane,
  children,
  density,
  action,
  onContextMenu,
  className,
}: {
  lane: EmailSignalLane;
  children: ReactNode;
  density: SignalStripDensity;
  action?: ReactNode;
  onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}) {
  const token = EMAIL_SIGNAL_LANE_TOKENS[lane];
  const stacked = density !== 'wide';
  return (
    <div
      data-signal-lane={lane}
      title={LANE_TITLES[lane]}
      onContextMenu={onContextMenu}
      className={cn(
        'group min-w-0 bg-background/30 px-2.5 py-1.5',
        onContextMenu && 'cursor-context-menu',
        className
      )}
    >
      <div
        className={cn(
          'min-w-0',
          stacked
            ? 'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1'
            : 'flex items-center gap-2'
        )}
      >
        <span
          aria-hidden
          title={token.label}
          className={cn(
            'shrink-0 select-none overflow-hidden text-ellipsis whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.12em]',
            stacked ? 'min-w-0 pt-0.5' : 'w-auto',
            token.labelClass
          )}
        >
          {density === 'compact' ? token.shortLabel : token.label}
        </span>
        <span className="sr-only">{token.label}</span>
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-wrap items-center gap-1.5 overflow-visible whitespace-normal',
            stacked && 'col-span-2 w-full'
          )}
          data-signal-chip-run={lane}
        >
          {children}
        </div>
        {/* Kebabs are quiet by default (change #267 D-POLISH-04): visible on
            lane hover or keyboard focus; always visible for coarse pointers
            where hover does not exist. The button keeps its layout slot so
            touch targets never shift. */}
        {action && (
          <span
            data-signal-lane-action
            className={cn(
              'shrink-0 opacity-0 transition-opacity duration-150',
              'focus-within:opacity-100 group-hover:opacity-100',
              '[@media(pointer:coarse)]:opacity-100',
              stacked && 'col-start-2 row-start-1'
            )}
          >
            {action}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * SignalStrip(slots) → the labelled triage strip.
 * Pure layout: the host pre-wires every affordance. Lane order is pinned to
 * EMAIL_SIGNAL_LANES; unpopulated lanes collapse (a plain email must never
 * show a dead "SIGNALS —" placeholder), and a fully empty strip renders
 * nothing at all — the v1 collapse contract, preserved.
 */
export function SignalStrip({
  who,
  what,
  signals,
  where,
  workflow,
  laneActions,
  onLaneContextMenu,
  className,
}: SignalStripProps) {
  const slots: Record<EmailSignalLane, ReactNode> = { who, what, signals, where };
  const activeLanes = EMAIL_SIGNAL_LANES.filter((lane) => Boolean(slots[lane]));
  const [ref, density] = useSignalStripDensity();
  if (activeLanes.length === 0 && !workflow) return null;

  return (
    <div
      ref={ref}
      data-testid="signal-strip"
      data-signal-density={density}
      className={cn(
        'mt-2 overflow-hidden rounded-lg border border-border/60 bg-muted/30 text-[11px]',
        className
      )}
    >
      {activeLanes.length > 0 && (
        <div
          data-signal-row="context"
          className={cn(
            'grid min-w-0 items-stretch divide-border/40',
            density === 'compact'
              ? 'grid-cols-1 divide-y'
              : density === 'medium'
                ? 'grid-cols-2'
                : 'grid-cols-4 divide-x'
          )}
        >
          {activeLanes.map((lane, index) => (
            <SignalLane
              key={lane}
              lane={lane}
              density={density}
              action={laneActions?.[lane]}
              className={cn(
                'min-w-0',
                density === 'medium' && index >= 2 && 'border-t border-border/40',
                density === 'medium' && index % 2 === 1 && 'border-l border-border/40',
              )}
              onContextMenu={
                onLaneContextMenu ? (event) => onLaneContextMenu(lane, event) : undefined
              }
            >
              {slots[lane]}
            </SignalLane>
          ))}
        </div>
      )}
      {workflow && (
        <div
          data-signal-row="workflow"
          className="min-w-0 border-t border-border/40"
        >
          <div data-signal-workflow className="min-w-0 bg-background/30">
            {workflow}
          </div>
        </div>
      )}
    </div>
  );
}

export default SignalStrip;
